import { motion } from "framer-motion";
import { Building2, Repeat2, Handshake, Check } from "lucide-react";
import SEO from "@/components/SEO";
import TrustMarquee from "@/components/TrustMarquee";
import { FadeUp, Chapter, CTAButton, EASE } from "@/components/Reveal";

const VORMEN = [
  {
    icon: Building2,
    title: "Portefeuille-opnames",
    text: "Meerdere woningen tegelijk laten opnemen: efficiënt gepland, uniform verwerkt en overzichtelijk opgeleverd.",
  },
  {
    icon: Repeat2,
    title: "Terugkerende opdrachten",
    text: "Bijvoorbeeld bij huurderswisselingen of verkoopmomenten binnen uw portefeuille. Wij houden de planning bij.",
  },
  {
    icon: Handshake,
    title: "Structurele samenwerking",
    text: "Vaste afspraken over planning, communicatie en tarieven voor een langdurige samenwerking.",
  },
];

const WAAROM = [
  "Eén vast aanspreekpunt voor al uw aanvragen",
  "Uniforme werkwijze volgens de geldende EP-methodiek",
  "Flexibele planning, afgestemd op uw portefeuille",
  "Heldere afspraken over tarieven en oplevering",
  "Registratie van elk label in EP-online",
];

export default function Zakelijk() {
  return (
    <>
      <SEO
        title="Energielabels voor makelaars en verhuurders | Directelabels"
        description="Directelabels ondersteunt makelaars, verhuurders en vastgoedpartijen met energielabels voor meerdere woningen, terugkerende opdrachten en structurele samenwerkingen in Rotterdam en Zuid-Holland."
        path="/zakelijk"
      />

      <section data-testid="zakelijk-hero" className="mx-auto max-w-7xl px-5 pb-16 pt-14 md:px-8 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
        >
          Zakelijk
        </motion.p>
        <h1 data-testid="zakelijk-title" className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
          {["Energielabels voor", "zakelijke partijen."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {i === 1 ? <span className="text-primary">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          Directelabels ondersteunt makelaars, verhuurders en vastgoedpartijen met energielabels voor meerdere woningen,
          terugkerende opdrachten en structurele samenwerkingen — met één vast aanspreekpunt en duidelijke afspraken.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-9"
        >
          <CTAButton to="/contact" testid="zakelijk-hero-cta">
            Zakelijke samenwerking bespreken
          </CTAButton>
        </motion.div>
      </section>

      <TrustMarquee />

      <section data-testid="zakelijk-vormen" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Chapter
          number="01"
          label="Samenwerkingsvormen"
          title="Hoe wij zakelijk samenwerken"
          description="Elke portefeuille is anders. Daarom stemmen wij de werkwijze af op uw situatie en planning."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {VORMEN.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.1}>
              <div
                data-testid={`zakelijk-card-${i}`}
                className="group h-full rounded-3xl border border-black/5 bg-white p-8 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-energy-pale text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                  <v.icon size={22} />
                </span>
                <h2 className="mt-6 font-display text-xl font-medium tracking-tight text-ink md:text-2xl">{v.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{v.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="zakelijk-waarom" className="border-y border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter
                number="02"
                label="Waarom Directelabels zakelijk"
                title="Betrouwbare partner voor uw portefeuille"
                description="Wij begrijpen dat timing en betrouwbaarheid cruciaal zijn bij verkoop- en verhuurprocessen."
              />
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {WAAROM.map((item, i) => (
                  <FadeUp key={item} delay={i * 0.08}>
                    <li data-testid={`zakelijk-waarom-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-background p-5">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span className="text-sm font-semibold leading-relaxed text-ink md:text-base">{item}</span>
                    </li>
                  </FadeUp>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="zakelijk-cta" className="bg-energy-deep text-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
            <div className="max-w-2xl">
              <FadeUp>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/60">Aan de slag</p>
              </FadeUp>
              <FadeUp delay={0.08}>
                <h2 className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight md:text-5xl">
                  Laten we de mogelijkheden bespreken
                </h2>
              </FadeUp>
              <FadeUp delay={0.16}>
                <p className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">
                  Vertel ons over uw portefeuille en wensen. Wij denken mee over een werkwijze die past bij uw
                  organisatie.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.2} className="shrink-0">
              <CTAButton to="/contact" testid="zakelijk-cta-button" variant="white">
                Zakelijke samenwerking bespreken
              </CTAButton>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
